import React, { useState, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { fadeIn } from '../../Functions/GlobalAnimations';
import { m } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { getMediaGallery } from '../../Store/Publication/gallery';
import Loading from '../../Components/Loading';
import SliderModal from '../../Components/SliderModal';

const KeraInMedia = () => {
	const [selectedMedia, setSelectedMedia] = useState(null);
	const modalTriggerRef = useRef(null);

	const { data: media, isLoading } = useQuery({
		queryKey: ['mediaGallery'],
		queryFn: () => getMediaGallery(),
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	});

	const openMediaModal = mediaItem => {
		setSelectedMedia(mediaItem);
		// Trigger the hidden button click after state is set
		setTimeout(() => {
			if (modalTriggerRef.current) {
				modalTriggerRef.current.click();
			}
		}, 50);
	};

	if (isLoading) return <Loading />;

	return (
		<>
			<m.div className="container mt-[200px] mb-[100px]">
				<Col xl={8} lg={10} className="text-start w-fit">
					<h3 className="font-serif text-[30px] mb-[2px] font-medium text-[#146084]">
						KERA in Media
					</h3>
					<m.div
						className="border-b-2 border-[#146084] w-2/5 sm:w-2/5"
						initial={{ scaleX: 0 }}
						animate={{ scaleX: 1 }}
						transition={{ duration: 0.5, delay: 0.2 }}
					/>
					<p className="text-sm sm:text-base text-gray-600 mt-4 leading-relaxed">
						A curated collection of media features, press coverage, and event
						highlights related to KERA.
					</p>
				</Col>

				<Row className="mt-16 sm:mt-20 lg:mt-28">
					{media?.data?.map((item, index) => {
						// Handle both single image and multiple images array
						const images =
							Array.isArray(item?.images) && item?.images?.length > 0
								? item?.images
								: [item?.images];

						const thumbnailImage = images?.[0]?.image;
						const imageCount = images?.length;

						return (
							<Col
								onClick={() => openMediaModal(item)}
								lg={4}
								md={6}
								sm={12}
								key={item.id || index}
								className="mb-8 lg:mb-10 cursor-pointer"
							>
								<m.div
									{...fadeIn}
									transition={{ delay: index * 0.1 }}
									className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 h-full flex flex-col"
								>
									<div className="relative overflow-hidden aspect-video group">
										<img
											src={`https://kera.enfonoerp.com/${thumbnailImage}`}
											alt={item.title || 'Media coverage'}
											className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
										/>

										{/* Gradient overlay */}
										<div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

										{/* View icon */}
										<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
											<div className="w-16 h-16 sm:w-20 sm:h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-2xl">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-8 w-8 sm:h-10 sm:w-10 text-[#146084]"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
													/>
												</svg>
											</div>
										</div>

										{/* Image count badge */}
										{imageCount > 1 && (
											<div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-3 w-3"
													viewBox="0 0 20 20"
													fill="currentColor"
												>
													<path
														fillRule="evenodd"
														d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
														clipRule="evenodd"
													/>
												</svg>
												{imageCount} Photos
											</div>
										)}

										{/* Media badge */}
										<div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-gradient-to-r from-[#146084] to-[#0d4a5f] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur-sm">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-3.5 w-3.5"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
												/>
											</svg>
											<span>Media</span>
										</div>

										{/* View Gallery text on hover */}
										<div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
											<p className="text-white text-center font-medium text-sm flex items-center justify-center gap-2">
												<span>View Gallery</span>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M13 7l5 5m0 0l-5 5m5-5H6"
													/>
												</svg>
											</p>
										</div>
									</div>

									{/* Card content */}
									<div className="p-5 sm:p-6 flex-1 flex flex-col">
										{item.title && (
											<h4 className="text-lg sm:text-xl font-semibold text-[#146084] mb-3 line-clamp-2 hover:text-[#0d4a5f] transition-colors duration-300">
												{item.title}
											</h4>
										)}
										{item.description && (
											<p className="text-sm sm:text-base text-gray-600 leading-relaxed line-clamp-3 flex-1">
												{item.description}
											</p>
										)}

										{/* View link */}
										<div className="mt-4 pt-4 border-t border-gray-100">
											<span className="text-[#146084] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all duration-300">
												<span>View Coverage</span>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
													/>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
													/>
												</svg>
											</span>
										</div>
									</div>
								</m.div>
							</Col>
						);
					})}
				</Row>

				{/* Empty state */}
				{!isLoading && (!media?.data || media?.data?.length === 0) && (
					<div className="text-center py-20">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-24 w-24 mx-auto text-gray-300 mb-4"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1}
								d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
							/>
						</svg>
						<p className="text-gray-500 text-lg">
							No media coverage available at the moment.
						</p>
					</div>
				)}
			</m.div>

			{/* Photo Slider Modal */}
			{selectedMedia && (
				<SliderModal
					photos={
						Array.isArray(selectedMedia.images) && selectedMedia.images.length > 0
							? selectedMedia?.images
							: [selectedMedia?.images]
					}
					description={selectedMedia.description}
					title={selectedMedia.title}
					triggerRef={modalTriggerRef}
				/>
			)}
		</>
	);
};

export default KeraInMedia;
